import { z } from 'zod'

// Le bandeau de confiance — la bande blanche d'arguments posée sous le hero de
// la page d'accueil, et qui clôt « Offres & Abonnements ».
//
// Il vivait à deux endroits : écrit en dur dans Accueil.jsx avec des icônes
// react-icons, saisi dans les textes des offres avec des caractères. Les deux
// avaient déjà divergé — « sur plus de 200 avis » d'un côté, « sur plus de 200
// avis clients » de l'autre, et quatre icônes différentes pour les mêmes
// quatre promesses. Un seul contenu, une seule clé de réglage, les deux pages
// le lisent : la divergence n'est plus possible.
//
// L'ordre du tableau porte l'ordre d'affichage, comme partout ailleurs dans
// ces contrats.

/** Clé du réglage dans `site_settings`. */
export const TRUST_BAR_KEY = 'trustBar'

/**
 * Les deux marqueurs de la phrase des avis. Nommés ici plutôt qu'écrits en
 * toutes lettres à chaque usage : le schéma, le rendu et l'écran de saisie
 * doivent parler des mêmes deux chaînes.
 */
export const MARQUEUR_NOTE = '{note}'
export const MARQUEUR_AVIS = '{avis}'

/** Un seul caractère, même convention que l'icône d'une famille du catalogue. */
const iconSchema = z.string().trim().min(1, 'L’icône est obligatoire')

/** Argument fixe : une icône, une phrase. */
export const trustTextItemSchema = z.object({
  kind: z.literal('texte'),
  icon: iconSchema,
  text: z
    .string()
    .trim()
    .min(1, 'Le texte est vide : cet argument n’a rien à afficher. Saisissez-le, ou retirez l’argument.'),
})

/**
 * L'argument des avis. Ses deux chiffres sont stockés à part du libellé, et
 * c'est tout l'intérêt de ne pas en faire du texte libre : une source
 * automatique peut les remplacer sans toucher à la phrase, et la phrase se
 * réécrit sans risquer d'effacer les chiffres.
 */
export const trustRatingItemSchema = z.object({
  kind: z.literal('note'),
  icon: iconSchema,
  rating: z
    .number({ invalid_type_error: 'Saisissez une note de 0 à 5, comme 4,9.' })
    .min(0, 'Saisissez une note de 0 à 5, comme 4,9.')
    .max(5, 'La note est sur 5 : saisissez une valeur de 0 à 5, comme 4,9.'),
  reviewCount: z
    .number({ invalid_type_error: 'Le nombre d’avis est un nombre entier, comme 214.' })
    .int('Le nombre d’avis est un nombre entier, comme 214.')
    .min(0, 'Le nombre d’avis est un nombre entier, comme 214.'),
  /**
   * La phrase, où `{note}` et `{avis}` sont remplacés par les deux chiffres.
   * Au moins l'un des deux est exigé : sans marqueur, les chiffres saisis
   * juste au-dessus ne s'afficheraient nulle part et la gérante croirait les
   * avoir publiés.
   */
  template: z
    .string()
    .trim()
    .min(1, 'La phrase est vide : cet argument n’a rien à afficher. Saisissez-la, ou retirez l’argument.')
    .refine(
      (valeur) => valeur.includes(MARQUEUR_NOTE) || valeur.includes(MARQUEUR_AVIS),
      'La phrase ne contient plus ni {note} ni {avis} : la note et le nombre d’avis saisis ci-dessus ne ' +
        's’afficheraient nulle part. L’enregistrement serait refusé — réinsérez au moins un des deux.',
    ),
})

export const trustItemSchema = z.discriminatedUnion('kind', [trustTextItemSchema, trustRatingItemSchema])

export const trustBarSchema = z.object({
  items: z.array(trustItemSchema),
})

export type TrustTextItem = z.infer<typeof trustTextItemSchema>
export type TrustRatingItem = z.infer<typeof trustRatingItemSchema>
export type TrustItem = z.infer<typeof trustItemSchema>
export type TrustBar = z.infer<typeof trustBarSchema>

/**
 * La note écrite comme on l'écrit en français : « 4,9 », et « 5 » sans
 * décimale inutile. Pas de `toLocaleString` — ce libellé doit être identique
 * dans l'aperçu de l'administration, dans les tests et sur le site, quelle que
 * soit la machine et son ICU.
 */
export function formatRating(rating: number): string {
  return String(rating).replace('.', ',')
}

/**
 * Le texte affiché pour un argument. Dans `contracts` et non dans la page,
 * pour la même raison que les libellés du catalogue et des offres :
 * l'administration en affiche l'aperçu, le site le rend, et les deux doivent
 * dire exactement la même chose.
 */
export function trustItemLabel(item: TrustItem): string {
  if (item.kind === 'texte') return item.text
  return item.template
    .replaceAll(MARQUEUR_NOTE, formatRating(item.rating))
    .replaceAll(MARQUEUR_AVIS, String(item.reviewCount))
}

/** Les champs d'un argument qui peuvent porter une erreur, par nom de champ. */
export type ErreursArgument = Partial<Record<'icon' | 'text' | 'rating' | 'reviewCount' | 'template', string>>

/**
 * Les erreurs d'un argument, champ par champ, telles que l'écran les affiche
 * pendant la saisie.
 *
 * C'est le SCHÉMA qui répond, pas une seconde règle écrite à côté : la gérante
 * voit exactement le message que le serveur lui opposerait, au moment où elle
 * tape, et une règle qui bouge ne bouge qu'à un endroit. La maquette montre
 * l'argument des avis bordé de rouge dès la frappe — sans cela, elle ne
 * l'apprendrait qu'en cliquant sur « Enregistrer ».
 */
export function erreursArgument(item: TrustItem): ErreursArgument {
  const parsed = trustItemSchema.safeParse(item)
  if (parsed.success) return {}

  const erreurs: ErreursArgument = {}
  for (const issue of parsed.error.issues) {
    const champ = issue.path[0]
    // Le premier message gagne : `min` puis `refine` sur le même champ
    // décrivent la même faute à deux stades, et c'est le plus précis qui
    // arrive en premier.
    if (
      (champ === 'icon' || champ === 'text' || champ === 'rating' || champ === 'reviewCount' || champ === 'template') &&
      !erreurs[champ]
    ) {
      erreurs[champ] = issue.message
    }
  }
  return erreurs
}

/**
 * Les quatre arguments actuellement en ligne. Repris tels quels des deux
 * bandeaux d'avant, dans la version la plus complète de chaque phrase.
 */
export const DEFAULT_TRUST_BAR: TrustBar = {
  items: [
    { kind: 'note', icon: '★', rating: 4.9, reviewCount: 200, template: 'Note {note}/5 sur plus de {avis} avis' },
    { kind: 'texte', icon: '✦', text: '10 minutes d’Auxerre' },
    { kind: 'texte', icon: '❀', text: 'Produits Baïja' },
    { kind: 'texte', icon: '⏱', text: 'Ouvert 5/7 jours' },
  ],
}
